import type { EmployeeRole } from '~/models/EmployeeRoles/Roles.enum'
import type { EmployeePermission } from '~/models/EmployeeRoles/Permissions.enum'

export function useUser() {
  const { user, idToken } = useAuth()
  const { getDocument, getDocuments, collection } = useFirestore()

  /**
   * Does the current user have admin rights. Only used for checking if you are able to override your own roles.
   * If you want to check for admin access you should use useRights().hasRole("Admin") 99% of the time.
   */
  const isAdmin = computed(() => !!idToken.value?.claims.admin)

  // #region User roles
  const overwrittenUserRoles = useSessionStorage<EmployeeRole[] | null>(
    'auth:group-override',
    () => null,
    {
      shallow: true,
      serializer: {
        read: raw => JSON.parse(raw),
        write: value => JSON.stringify(value),
      },
    },
  )

  /**
   * User role
   */
  const tokenUserRoles = computed<EmployeeRole[]>(() => idToken.value?.claims.userGroups ?? [])

  const userRoles = computed<EmployeeRole[]>({
    get: () => {
      if (overwrittenUserRoles.value)
        return overwrittenUserRoles.value

      return tokenUserRoles.value
    },
    set: (value: EmployeeRole[]) => {
      if (!isAdmin.value) {
        throw new Error('Only admins can override user groups')
      }

      // check to see if id token contains the same values, if so remove overwrittenUserGroups
      if (value.length > 0 && value.every(group => tokenUserRoles.value.includes(group))) {
        overwrittenUserRoles.value = null
        return
      }

      overwrittenUserRoles.value = value
    },
  })

  const hasOverwrittenUserRoles = computed(() => !!overwrittenUserRoles.value)
  // #endregion

  /**
   * All permissions attached to this user.
   */
  const permissions = ref<EmployeePermission[]>([])

  async function fetchPermissions() {
    if (!user.value) {
      return
    }

    if (overwrittenUserRoles.value) {
      const roles = await getDocuments<
        { id: string, permissions: string[] }
      >(collection('EmployeeRoles'))

      permissions.value = overwrittenUserRoles.value.flatMap(role =>
        roles.find(x => x.id === role)?.permissions ?? [],
      )

      return
    }

    const role = await getDocument<{ permissions: EmployeePermission[] }>(
      [
        'EmployeeUsers',
        user.value?.uid,
        'EmployeeUserPermissions',
        'Permissions',
      ],
    )

    permissions.value = role?.permissions ?? []
  }

  void fetchPermissions()
  watch(userRoles, () => void fetchPermissions())

  return {
    userRoles,
    isAdmin,
    user,
    permissions,
    hasOverwrittenUserRoles,
  }
}
